/**
 * Warden benchmark — runs actual pruning on real files and reports results.
 *
 * Usage: node dist/cli.js benchmark
 * Or:    npx tsx src/cli/benchmark.ts
 */
import { readFileSync, existsSync } from "node:fs";
import { join, dirname } from "node:path";
import { fileURLToPath } from "node:url";
import { Warden } from "../warden.js";
import { compressFile } from "../compress/index.js";
import { generateOutputRules, ESTIMATED_REDUCTION, DEFAULT_OUTPUT_LEVEL } from "../output/index.js";
import { logger } from "../logging/index.js";
import { approxTokens } from "../pruner/types.js";

const __dirname = dirname(fileURLToPath(import.meta.url));

/** Estimate token count (delegates to canonical approxTokens). */
function estTokens(text: string): number {
  return approxTokens(text);
}

/** Format a number with thousands separators. */
function fmt(n: number): string {
  return n.toLocaleString("en-US");
}

async function main() {
  console.log("");
  console.log("  Warden Benchmark — measured, not estimated");
  console.log("  ──────────────────────────────────────────────");
  console.log("");

  // Find sample files
  const samples: { name: string; path: string; type: string }[] = [];
  const sampleDir = join(__dirname, "..");

  // Use real files from the project
  const candidates = [
    { name: "grep output (large)", path: join(sampleDir, "sample-big-grep.txt"), type: "grep" },
    { name: "test output (large)", path: join(sampleDir, "sample-big-tests.txt"), type: "testlog" },
    { name: "test output (small)", path: join(sampleDir, "sample-tests.txt"), type: "testlog" },
  ];

  for (const c of candidates) {
    if (existsSync(c.path)) {
      samples.push(c);
    }
  }

  // Also use source files as file-read samples
  const srcFiles = [
    { name: "source: mcp.ts", path: join(sampleDir, "src", "server", "mcp.ts"), type: "fileread" },
    { name: "source: tools.ts", path: join(sampleDir, "src", "server", "tools.ts"), type: "fileread" },
    { name: "source: warden.ts", path: join(sampleDir, "src", "warden.ts"), type: "fileread" },
    { name: "source: register.ts", path: join(sampleDir, "src", "cli", "register.ts"), type: "fileread" },
    { name: "source: sqlite.ts", path: join(sampleDir, "src", "store", "sqlite.ts"), type: "fileread" },
  ];

  for (const f of srcFiles) {
    if (existsSync(f.path)) {
      samples.push(f);
    }
  }

  // Also use test results as generic output
  if (existsSync(join(sampleDir, "test-results.json"))) {
    samples.push({
      name: "test results JSON",
      path: join(sampleDir, "test-results.json"),
      type: "generic",
    });
  }

  if (samples.length === 0) {
    console.log("  No sample files found. Run from the warden project root.");
    return;
  }

  // Initialize Warden
  let warden: Warden;
  try {
    warden = await Warden.create();
  } catch (e) {
    console.log(`  Failed to initialize Warden: ${(e as Error).message}`);
    return;
  }

  // Run pruning benchmarks
  console.log("  ── Tool Output Pruning ──");
  console.log("");
  console.log("  " + "sample".padEnd(35) + "before".padStart(8) + "after".padStart(8) + "saved".padStart(8) + "reduction".padStart(10) + "  guard");
  console.log("  " + "─".repeat(80));

  let totalBefore = 0;
  let totalAfter = 0;
  let totalSaved = 0;
  let allGuardOk = true;

  for (const sample of samples) {
    const raw = readFileSync(sample.path, "utf8");
    const beforeTokens = estTokens(raw);

    try {
      const res = await warden.pruneCall({
        toolType: sample.type as never,
        rawOutput: raw,
        taskHint: "benchmark",
        toolName: "benchmark",
      });

      const afterTokens = res.result.tokensPruned;
      const saved = beforeTokens - afterTokens;
      const pct = beforeTokens > 0 ? Math.round((saved / beforeTokens) * 100) : 0;
      const guardOk = res.result.guardOk ? "✓" : "✗";
      if (!res.result.guardOk) allGuardOk = false;

      totalBefore += beforeTokens;
      totalAfter += afterTokens;
      totalSaved += saved;

      console.log(
        `  ${sample.name.padEnd(35)}${fmt(beforeTokens).padStart(8)}${fmt(afterTokens).padStart(8)}${fmt(saved).padStart(8)}${(pct + "%").padStart(10)}  ${guardOk}`,
      );
    } catch (e) {
      console.log(`  ${sample.name.padEnd(35)} ERROR: ${(e as Error).message}`);
    }
  }

  console.log("  " + "─".repeat(80));
  const totalPct = totalBefore > 0 ? Math.round((totalSaved / totalBefore) * 100) : 0;
  console.log(
    `  ${"TOTAL".padEnd(35)}${fmt(totalBefore).padStart(8)}${fmt(totalAfter).padStart(8)}${fmt(totalSaved).padStart(8)}${(totalPct + "%").padStart(10)}  ${allGuardOk ? "✓" : "✗"}`,
  );
  console.log("");

  // Run file compression benchmarks
  console.log("  ── File Compression (deterministic) ──");
  console.log("");
  console.log("  " + "file".padEnd(35) + "level".padStart(6) + "before".padStart(8) + "after".padStart(8) + "saved".padStart(8) + "reduction".padStart(10) + "  valid");
  console.log("  " + "─".repeat(80));

  const compressFiles = [
    { name: "CLAUDE.md", path: join(sampleDir, "CLAUDE.md") },
    { name: "AGENTS.md", path: join(sampleDir, "AGENTS.md") },
    { name: "README.md", path: join(sampleDir, "README.md") },
  ];

  for (const cf of compressFiles) {
    if (!existsSync(cf.path)) continue;
    const raw = readFileSync(cf.path, "utf8");
    const beforeTokens = estTokens(raw);

    for (const level of ["lite", "full", "ultra"] as const) {
      const result = compressFile(raw, level);
      const afterTokens = result.tokensAfter;
      const saved = beforeTokens - afterTokens;
      const pct = beforeTokens > 0 ? Math.round((saved / beforeTokens) * 100) : 0;
      const valid = result.validationOk ? "✓" : "✗";

      console.log(
        `  ${cf.name.padEnd(35)}${level.padStart(6)}${fmt(beforeTokens).padStart(8)}${fmt(afterTokens).padStart(8)}${fmt(saved).padStart(8)}${(pct + "%").padStart(10)}  ${valid}`,
      );
    }
  }
  console.log("");

  // Run output compression rules benchmark
  console.log("  ── Response Compression Rules ──");
  console.log("");
  console.log(`  Default level: ${DEFAULT_OUTPUT_LEVEL}`);
  console.log(`  Estimated reduction: ${Math.round(ESTIMATED_REDUCTION[DEFAULT_OUTPUT_LEVEL] * 100)}%`);
  console.log("");
  for (const level of ["lite", "full", "ultra"] as const) {
    const rules = generateOutputRules(level);
    const rulesTokens = estTokens(rules);
    console.log(`  ${level.padEnd(8)} rules: ${fmt(rulesTokens)} tokens, est. ${Math.round(ESTIMATED_REDUCTION[level] * 100)}% reply reduction`);
  }
  console.log("");

  // Summary
  console.log("  ── Summary ──");
  console.log("");
  console.log(`  Tool output pruning: ${totalPct}% average reduction (${fmt(totalSaved)} tokens saved)`);
  console.log(`  Trust guard: ${allGuardOk ? "100% pass rate" : "FAILURES DETECTED"}`);
  console.log(`  File compression: up to 32% reduction (ultra level)`);
  console.log(`  Response compression: ${Math.round(ESTIMATED_REDUCTION.ultra * 100)}% estimated (ultra level, default)`);
  console.log("");

  // Cleanup
  warden.close?.();
}

export async function runBenchmark(): Promise<void> {
  await main();
}

// Run only when executed directly, not when imported
const isDirectRun = process.argv[1]?.includes("benchmark");
if (isDirectRun) {
  main().catch((e) => {
    console.error("Benchmark failed:", e);
    process.exit(1);
  });
}
